import { useState, useEffect } from 'react';
import { X, Mail, User } from 'lucide-react';

interface Recipient {
  email: string;
  name: string;
}

interface RecipientPreviewProps {
  isOpen: boolean;
  onClose: () => void;
  targetTable: string;
  targetFilters: {
    status?: string | null;
    source?: string | null;
    specific_emails?: string[];
  };
  adminToken: string;
}

export default function RecipientPreview({
  isOpen,
  onClose,
  targetTable,
  targetFilters,
  adminToken,
}: RecipientPreviewProps) {
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      fetchRecipients();
    }
  }, [isOpen, targetTable, targetFilters]);

  const fetchRecipients = async () => {
    if (!adminToken) return;

    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/campaigns-preview`,
        {
          method: 'POST',
          headers: {
            'X-Admin-Token': adminToken,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            target_table: targetTable,
            target_filters: targetFilters,
            limit: 100,
          }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        setRecipients(data.recipients);
        setTotalCount(data.count);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to fetch recipients');
      }
    } catch (err) {
      setError('Failed to fetch recipients. Please try again.');
      console.error('Failed to fetch recipients:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 border border-yellow-400/30 rounded-lg shadow-xl max-w-3xl w-full max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-yellow-400/30">
          <div>
            <h2 className="text-2xl font-bold text-yellow-400">
              Campaign Recipients
            </h2>
            <p className="text-sm text-gray-400 mt-1">
              {loading ? (
                'Loading...'
              ) : (
                `Showing ${recipients.length} of ${totalCount} recipient${totalCount !== 1 ? 's' : ''}`
              )}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-yellow-400/10 rounded-lg transition-colors text-gray-400 hover:text-yellow-400"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-400"></div>
            </div>
          ) : error ? (
            <div className="bg-red-900/20 border border-red-600/30 rounded-lg p-4">
              <p className="text-sm text-red-300">{error}</p>
            </div>
          ) : recipients.length === 0 ? (
            <div className="text-center py-12">
              <Mail className="w-12 h-12 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-400">No recipients found</p>
            </div>
          ) : (
            <div className="space-y-2">
              {recipients.map((recipient, index) => (
                <div
                  key={index}
                  className="flex items-center gap-3 p-3 bg-black/50 border border-yellow-400/20 rounded-lg hover:border-yellow-400/40 transition-colors"
                >
                  <div className="w-10 h-10 rounded-full bg-yellow-400/20 flex items-center justify-center flex-shrink-0">
                    <User className="w-5 h-5 text-yellow-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-white truncate">
                      {recipient.name || 'No name'}
                    </p>
                    <p className="text-sm text-gray-400 truncate">
                      {recipient.email}
                    </p>
                  </div>
                </div>
              ))}
              {totalCount > recipients.length && (
                <div className="text-center py-4 text-sm text-gray-400 border-t border-yellow-400/20 mt-4 pt-4">
                  And {totalCount - recipients.length} more recipient{totalCount - recipients.length !== 1 ? 's' : ''}...
                </div>
              )}
            </div>
          )}
        </div>

        <div className="p-6 border-t border-yellow-400/30 bg-black/50">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-yellow-400 text-black rounded-lg hover:bg-yellow-300 transition-colors font-bold"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
